import React, { useMemo } from 'react';
import { Pressable, Text, ViewStyle } from 'react-native';
import { getStyles } from './ButtonText.styles';
import { useTheme } from '../../hooks/useTheme';
import { getContrastYIQ } from '@oolio-group/design-common';
import { ProgressDots } from '../ProgressDots/ProgressDots';
import { IconState } from '../Icon/IconState';

interface Props {
  id: string;
  label: string;
  loading?: boolean;
  success?: boolean;
  disabled?: boolean;
  buttonStyle?: 'primary' | 'secondary';
  onPress: () => void;
  containerStyles?: ViewStyle | ViewStyle[];
}

export const ButtonText: React.FC<Props> = ({
  id,
  label,
  loading = false,
  success = false,
  disabled = false,
  buttonStyle = 'secondary',
  onPress,
  containerStyles,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);

  const isPrimary = buttonStyle === 'primary';
  const fgColor = getContrastYIQ(colors.primary);

  // Dim only while loading/explicitly disabled — keep the success state vibrant.
  const isDisabled = disabled || loading;
  // Block presses during the success animation too, without dimming.
  const isInteractionBlocked = isDisabled || success;

  return (
    <Pressable
      testID={id}
      onPress={onPress}
      disabled={isInteractionBlocked}
      style={({ pressed }) => [
        styles.container,
        isPrimary && styles.primary,
        containerStyles,
        pressed && styles.pressed,
        isDisabled && styles.disabled,
      ]}
    >
      {loading ? (
        <ProgressDots size={26} color={colors.placeholder} />
      ) : success ? (
        <IconState color={isPrimary ? fgColor : colors.icon} />
      ) : (
        <Text
          style={[
            styles.label,
            isPrimary && styles.labelPrimary,
            isDisabled && styles.labelDisabled,
          ]}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
};
